import React, { useState, useRef, useCallback, useEffect } from 'react';
import { TrendingUp, Play, Loader2, CheckCircle2, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';

interface LogEntry {
  timestamp: string;
  type: string;
  message: string;
  data?: any;
}

const TYPE_COLORS: Record<string, string> = {
  info: 'bg-zinc-600 text-zinc-200',
  accounts: 'bg-blue-600 text-blue-100',
  fetch: 'bg-cyan-600 text-cyan-100',
  posts: 'bg-yellow-600 text-yellow-100',
  prompt: 'bg-purple-600 text-purple-100',
  response: 'bg-green-600 text-green-100',
  validation: 'bg-orange-600 text-orange-100',
  result: 'bg-emerald-600 text-emerald-100',
  error: 'bg-red-600 text-red-100',
  complete: 'bg-green-500 text-white',
};

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function LogEntryRow({ entry }: { entry: LogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasData = entry.data !== undefined && entry.data !== null;

  return (
    <div className="group">
      <div
        className={cn(
          "flex items-start gap-2 px-3 py-1.5 font-mono text-xs hover:bg-white/5 transition-colors",
          hasData && "cursor-pointer"
        )}
        onClick={() => hasData && setExpanded(!expanded)}
      >
        <span className="text-zinc-500 shrink-0 select-none">[{formatTime(entry.timestamp)}]</span>
        <Badge className={cn("text-[9px] font-black uppercase px-1.5 py-0 h-4 shrink-0", TYPE_COLORS[entry.type] || 'bg-zinc-600')}>
          {entry.type}
        </Badge>
        <span className={cn(
          "flex-1 break-words",
          entry.type === 'error' ? 'text-red-400' :
          entry.type === 'complete' ? 'text-green-400' :
          entry.message.includes('REJECTED') ? 'text-orange-400' :
          entry.message.includes('ACCEPTED') ? 'text-emerald-400' :
          'text-zinc-200'
        )}>
          {entry.message}
        </span>
        {hasData && (
          <span className="text-zinc-500 shrink-0">
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </span>
        )}
      </div>
      {expanded && hasData && (
        <div className="ml-[72px] mr-3 mb-2 p-2 bg-zinc-900 rounded border border-zinc-700 overflow-x-auto">
          <pre className="text-[10px] text-zinc-400 whitespace-pre-wrap break-words max-h-[400px] overflow-y-auto">
            {typeof entry.data === 'string' ? entry.data : JSON.stringify(entry.data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function TrendFetchLive() {
  const [status, setStatus] = useState<'idle' | 'running' | 'completed' | 'error'>('idle');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [forceRefresh, setForceRefresh] = useState(true);
  const [clearFirst, setClearFirst] = useState(false);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const scrollToBottom = useCallback(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [logs, scrollToBottom]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const startFetch = async () => {
    setStatus('running');
    setLogs([]);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/admin/fetch-trends-live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ forceRefresh, clearFirst }),
      });

      if (!res.ok) {
        setStatus('error');
        setLogs([{ timestamp: new Date().toISOString(), type: 'error', message: `Failed to start: ${res.statusText}` }]);
        return;
      }

      const { sessionId } = await res.json();

      // Connect to SSE
      const es = new EventSource(`/api/admin/trend-fetch-logs/${sessionId}?token=${token}`);
      eventSourceRef.current = es;

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'close') {
            setStatus(data.status === 'error' ? 'error' : 'completed');
            es.close();
            eventSourceRef.current = null;
            return;
          }

          setLogs(prev => [...prev, data]);
        } catch (e) {
          console.error('SSE parse error:', e);
        }
      };

      es.onerror = () => {
        setStatus(prev => prev === 'running' ? 'error' : prev);
        es.close();
        eventSourceRef.current = null;
      };
    } catch (err) {
      setStatus('error');
      setLogs([{ timestamp: new Date().toISOString(), type: 'error', message: `Network error: ${(err as Error).message}` }]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-primary">
            <TrendingUp className="h-5 w-5" />
            <h2 className="text-2xl font-black text-foreground tracking-tight">Trend Fetch Live</h2>
          </div>
          <p className="text-muted-foreground text-sm">Real-time view of the trend detection pipeline</p>
        </div>
        <Badge className={cn(
          "font-black text-xs px-3 py-1",
          status === 'idle' ? 'bg-zinc-600' :
          status === 'running' ? 'bg-blue-600 animate-pulse' :
          status === 'completed' ? 'bg-green-600' :
          'bg-red-600'
        )}>
          {status === 'idle' ? 'IDLE' :
           status === 'running' ? 'RUNNING' :
           status === 'completed' ? 'COMPLETE' : 'ERROR'}
        </Badge>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border">
        <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
          <input
            type="checkbox"
            checked={forceRefresh}
            onChange={(e) => setForceRefresh(e.target.checked)}
            disabled={status === 'running'}
            className="rounded"
          />
          Force refresh (skip cache)
        </label>
        <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
          <input
            type="checkbox"
            checked={clearFirst}
            onChange={(e) => setClearFirst(e.target.checked)}
            disabled={status === 'running'}
            className="rounded"
          />
          Clear existing trends
        </label>
        <div className="flex-1" />
        <Button
          onClick={startFetch}
          disabled={status === 'running'}
          className="font-black"
        >
          {status === 'running' ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running...</>
          ) : (
            <><Play className="h-4 w-4 mr-2" /> Start Fetch</>
          )}
        </Button>
      </div>

      {/* Log Terminal */}
      <div className="rounded-xl border border-zinc-700 overflow-hidden shadow-2xl">
        <div className="bg-zinc-800 px-4 py-2 flex items-center gap-2 border-b border-zinc-700">
          <div className="flex gap-1.5">
            <div className="h-3 w-3 rounded-full bg-red-500" />
            <div className="h-3 w-3 rounded-full bg-yellow-500" />
            <div className="h-3 w-3 rounded-full bg-green-500" />
          </div>
          <span className="text-zinc-400 text-xs font-mono ml-2">trend-fetch-live</span>
          <span className="text-zinc-600 text-xs font-mono ml-auto">{logs.length} entries</span>
        </div>
        <div
          ref={logContainerRef}
          className="bg-zinc-950 min-h-[400px] max-h-[600px] overflow-y-auto"
        >
          {logs.length === 0 ? (
            <div className="flex items-center justify-center h-[400px] text-zinc-600 text-sm font-mono">
              {status === 'idle' ? 'Click "Start Fetch" to begin...' : 'Waiting for logs...'}
            </div>
          ) : (
            <div className="py-2">
              {logs.map((entry, i) => (
                <LogEntryRow key={i} entry={entry} />
              ))}
              {status === 'completed' && (
                <div className="flex items-center gap-2 px-3 py-2 text-green-400 font-mono text-xs border-t border-zinc-800 mt-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Process completed successfully
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-center gap-2 px-3 py-2 text-red-400 font-mono text-xs border-t border-zinc-800 mt-2">
                  <XCircle className="h-4 w-4" />
                  Process ended with errors
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
